import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Star, Zap, Check, Crown, QrCode, TrendingUp, ExternalLink } from 'lucide-react';
import { marketApi, subscriptionApi, orderApi } from '@/api';
import type { System, Plan } from '@/api/market';
import type { Subscription } from '@/api/subscription';
import type { Order, PayParams } from '@/api/order';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  alertDialogDarkClass,
  btnOutlineNeon,
  btnPrimaryNeon,
} from '@/lib/admin-ui';
import { useTenantPermission } from '@/hooks/use-tenant-permission';

export function MarketPage() {
  const canMarket = useTenantPermission('tenant:market:view');
  const [systems, setSystems] = useState<System[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [selectedPlans, setSelectedPlans] = useState<Record<string, string>>({});
  
  // 支付相关状态
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [payParams, setPayParams] = useState<PayParams | null>(null);
  const [paying, setPaying] = useState(false);
  const [selectedPlanPrice, setSelectedPlanPrice] = useState<number>(0);

  const loadData = useCallback(async () => {
    if (!canMarket) {
      setSystems([]);
      setSubscriptions([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // 并行获取系统和订阅列表
      const [systemsData, subscriptionsData] = await Promise.all([
        marketApi.getSystems(),
        subscriptionApi.getMySubscriptions(),
      ]);

      // 将价格从分转换为元
      const systemsArray = (Array.isArray(systemsData) ? systemsData : []).map((system: System) => ({
        ...system,
        plans: system.plans?.map(plan => ({
          ...plan,
          price: plan.price / 100,
        })),
      }));
      const subsArray = Array.isArray(subscriptionsData) ? subscriptionsData : [];

      setSystems(systemsArray);
      setSubscriptions(subsArray);
      
      // 初始化每个系统的默认选中套餐为第一个
      const defaultSelections: Record<string, string> = {};
      systemsArray.forEach((system: System) => {
        if (system.plans && system.plans.length > 0) {
          // 如果已订阅，默认选中“可升级版本”中的最低档（价格最接近当前档）
          const existingSub = subsArray.find((sub: Subscription) => sub.systemId === system.id);
          if (existingSub) {
            const currentPlan = system.plans.find((p) => p.id === existingSub.planId);
            const currentPrice = currentPlan?.price ?? -Infinity;
            const upgradeCandidates = system.plans
              .filter((p) => p.price > currentPrice)
              .sort((a, b) => a.price - b.price);

            // 有可升级版本则默认选中最便宜的可升级项；否则回退到当前订阅版本
            defaultSelections[system.id] = upgradeCandidates[0]?.id ?? existingSub.planId;
          } else {
            defaultSelections[system.id] = system.plans[0].id;
          }
        }
      });
      setSelectedPlans(defaultSelections);
    } catch (error) {
      console.error('加载数据失败:', error);
      setSystems([]);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [canMarket]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // 检查系统是否已订阅
  function isSubscribed(systemId: string): boolean {
    return subscriptions.some(sub => sub.systemId === systemId);
  }

  // 获取系统的当前订阅
  function getSubscription(systemId: string): Subscription | undefined {
    return subscriptions.find(sub => sub.systemId === systemId);
  }

  function handlePlanSelect(systemId: string, planId: string) {
    if (!canMarket) return;
    // 允许已订阅用户选择其他版本用于升级
    setSelectedPlans(prev => ({
      ...prev,
      [systemId]: planId
    }));
  }

  async function handlePurchase(system: System) {
    if (!canMarket) return;
    if (!system.plans || system.plans.length === 0) {
      toast.error('该系统暂无可购买版本');
      return;
    }

    const selectedPlanId = selectedPlans[system.id];
    if (!selectedPlanId) {
      toast.error('请先选择订阅版本');
      return;
    }

    const plan = system.plans.find(p => p.id === selectedPlanId);
    if (!plan) {
      toast.error('所选套餐不存在');
      return;
    }

    // 如果已订阅，检查是否选择了更高价格的版本（升级）
    const subscription = getSubscription(system.id);
    if (subscription) {
      const currentPlan = system.plans.find(p => p.id === subscription.planId);
      if (currentPlan && plan.price <= currentPlan.price) {
        toast.info('请选择价格更高的版本进行升级');
        return;
      }
    }

    setPurchasing(system.id);
    try {
      // 1. 创建订单
      const order = await orderApi.createOrder({
        systemId: system.id,
        planId: plan.id,
      });
      
      setCurrentOrder(order);
      
      // 2. 获取支付参数
      const params = await orderApi.getPayParams(order.id);
      setPayParams(params);
      
      // 3. 记录选择的计划价格（用于显示）
      setSelectedPlanPrice(plan.price);
      
      // 4. 打开支付弹窗
      setPayDialogOpen(true);
    } catch (error: any) {
      console.error('创建订单失败:', error);
      toast.error(`创建订单失败：${error.response?.data?.message || error.message}`);
    } finally {
      setPurchasing(null);
    }
  }

  // 模拟微信支付
  async function handleMockPay(success = true) {
    if (!currentOrder) return;
    
    setPaying(true);
    try {
      const result = await orderApi.mockPay(currentOrder.id, success);
      
      if (success) {
        toast.success('支付成功！');
        setPayDialogOpen(false);
        loadData(); // 刷新数据
      } else {
        toast.error('支付失败，请重试');
      }
    } catch (error: any) {
      console.error('支付失败:', error);
      toast.error(`支付失败：${error.response?.data?.message || error.message}`);
    } finally {
      setPaying(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">应用市场</h1>
          <p className="mt-1 text-sm text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!canMarket && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          当前角色无应用市场权限（<code className="font-mono text-amber-200/90">tenant:market:view</code>
          ），无法浏览或购买。请联系租户管理员调整角色权限。
        </div>
      )}
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">应用市场</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          浏览和购买系统订阅
        </p>
      </div>

      {/* 筛选栏 */}
      <div className="flex gap-2">
        <Badge className="cursor-pointer" variant="default">
          全部
        </Badge>
        <Badge className="cursor-pointer" variant="outline">
          热门推荐
        </Badge>
        <Badge className="cursor-pointer" variant="outline">
          新上架
        </Badge>
      </div>

      {/* 系统列表 */}
      {systems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShoppingCart className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">暂无应用</h3>
            <p className="text-sm text-muted-foreground">
              应用市场正在持续更新中，敬请期待
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {systems.map((system) => {
            const Icon = system.icon === 'Zap' ? Zap : system.icon === 'Star' ? Star : ShoppingCart;
            const selectedPlanId = selectedPlans[system.id];
            const selectedPlan = system.plans?.find(p => p.id === selectedPlanId);
            const subscribed = isSubscribed(system.id);
            const subscription = getSubscription(system.id);
            
            // 过滤掉已订阅的版本（只显示更高价格的版本用于升级）
            const availablePlans = subscribed && subscription
              ? system.plans?.filter(p => p.price > (system.plans?.find(sp => sp.id === subscription.planId)?.price || 0))
              : system.plans;
            
            return (
              <Card key={system.id} className={`flex flex-col ${subscribed ? 'ring-2 ring-primary' : ''} relative overflow-visible`}>
                {/* 右上角当前订阅版本和到期时间角标 */}
                {subscribed && subscription && (
                  <div className="absolute -top-2 -right-2 z-10 bg-primary text-primary-foreground text-xs px-2 py-1.5 rounded-md shadow-lg flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-1 font-medium">
                      <Crown className="w-3 h-3" />
                      {subscription.plan?.name}
                    </div>
                    <div className="text-primary-foreground/80 text-[10px]">
                      到期：{new Date(subscription.endAt).toLocaleDateString('zh-CN')}
                    </div>
                  </div>
                )}
                
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{system.name}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium">4.8</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          (128 人在用)
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 space-y-4">
                  <CardDescription className="text-sm">
                    {system.description || '暂无描述'}
                  </CardDescription>
                  
                  {/* 订阅版本选择 */}
                  {availablePlans && availablePlans.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">
                        {subscribed ? '可升级版本：' : '选择订阅版本：'}
                      </div>
                      <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
                        {availablePlans.map((plan) => {
                          const isSelected = selectedPlanId === plan.id;
                          
                          return (
                            <button
                              key={plan.id}
                              type="button"
                              disabled={!canMarket}
                              onClick={() => handlePlanSelect(system.id, plan.id)}
                              className={`px-3 py-1.5 text-sm rounded-md border transition-colors whitespace-nowrap ${
                                isSelected
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background hover:bg-muted border-input'
                              }`}
                            >
                              <div className="flex items-center gap-1">
                                {isSelected && (
                                  <Check className="w-3 h-3" />
                                )}
                                {plan.name}
                              </div>
                              <div className="text-xs opacity-80">
                                ¥{plan.price}/月
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* 无可用升级版本提示 */}
                  {subscribed && (!availablePlans || availablePlans.length === 0) && (
                    <div className="text-sm text-muted-foreground">
                      当前已是最高版本，无需升级
                    </div>
                  )}
                </CardContent>
                <CardFooter className="flex items-center justify-end border-t pt-4">
                  <div className="flex items-center gap-2">
                    {/* 已订阅且未过期显示进入系统按钮 */}
                    {canMarket &&
                      subscribed &&
                      subscription &&
                      new Date(subscription.endAt) > new Date() &&
                      system.url && (
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className={btnPrimaryNeon}
                        onClick={() => system.url && window.open(system.url, '_blank')}
                      >
                        <ExternalLink className="mr-1 h-4 w-4" />
                        进入系统
                      </Button>
                    )}
                    {/* 可升级时显示升级按钮 */}
                    {canMarket &&
                      subscribed &&
                      availablePlans &&
                      availablePlans.length > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={btnOutlineNeon}
                        disabled={purchasing === system.id || !selectedPlan}
                        onClick={() => handlePurchase(system)}
                      >
                        <TrendingUp className="mr-1 h-4 w-4" />
                        升级
                      </Button>
                    )}
                    {/* 未订阅显示购买按钮 */}
                    {canMarket && !subscribed && (
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className={btnPrimaryNeon}
                        disabled={purchasing === system.id || !selectedPlan}
                        onClick={() => handlePurchase(system)}
                      >
                        <ShoppingCart className="mr-1 h-4 w-4" />
                        {purchasing === system.id ? '创建订单中...' : '购买'}
                      </Button>
                    )}
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {/* 微信支付弹窗 */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent
          className={cn(
            'max-w-md',
            alertDialogDarkClass,
            '[&>button]:rounded-lg [&>button]:border [&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400 [&>button]:opacity-100 [&>button]:hover:bg-cyan-500/10 [&>button]:hover:text-cyan-200',
          )}
        >
          <DialogHeader>
            <DialogTitle className="text-zinc-50">微信支付</DialogTitle>
          </DialogHeader>
          
          {payParams && (
            <div className="space-y-6 py-4">
              {/* 订单信息 */}
              <div className="text-center space-y-2">
                <div className="text-sm text-muted-foreground">
                  {payParams.systemName} - {payParams.planName}
                </div>
                <div className="text-3xl font-bold text-cyan-300">
                  ¥{selectedPlanPrice}/月
                </div>
              </div>
              
              {/* 二维码区域 */}
              <div className="flex flex-col items-center space-y-4">
                <div className="flex h-48 w-48 items-center justify-center rounded-lg border-2 border-dashed border-zinc-600 bg-zinc-900/80">
                  <div className="text-center">
                    <QrCode className="mx-auto mb-2 h-16 w-16 text-zinc-500" />
                    <div className="text-xs text-zinc-500">模拟微信支付二维码</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  请使用微信扫一扫完成支付
                </p>
              </div>
              
              {/* 模拟支付按钮 */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className={cn('flex-1', btnOutlineNeon)}
                  onClick={() => handleMockPay(false)}
                  disabled={paying}
                >
                  模拟支付失败
                </Button>
                <Button
                  type="button"
                  className={cn('flex-1', btnPrimaryNeon)}
                  onClick={() => handleMockPay(true)}
                  disabled={paying}
                >
                  {paying ? '支付中...' : '模拟支付成功'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
